import React from "react";
import { FormSection } from "../../common/FormSection";
import { PersonalisedLearningSupportPlanData } from "src/models/form-types";

interface LearningSectionProps {
  formData: PersonalisedLearningSupportPlanData;
  onTextChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  onCheckboxChange: (
    name: string,
    checked: boolean,
    category: keyof PersonalisedLearningSupportPlanData
  ) => void;
  className?: string;
}

// Define the support type options
const adhdTypeOptions = [
  { value: "Inattentive type", label: "Inattentive type" },
  { value: "Hyperactive type", label: "Hyperactive type" },
  { value: "Combined type", label: "Combined type" },
];

// Define the support level options
const autismLevelOptions = [
  { value: "Level 1", label: "Level 1" },
  { value: "Level 2", label: "Level 2" },
  { value: "Level 3", label: "Level 3" },
];

export const LearningSection: React.FC<LearningSectionProps> = ({
  formData,
  onTextChange,
  onCheckboxChange,
  className,
}) => {
  const handleLearningCheckboxChange = (name: string, checked: boolean) => {
    onCheckboxChange(name, checked, "learningSupport");
  };

  const handlePlanCheckboxChange = (name: string, checked: boolean) => {
    onCheckboxChange(
      name,
      checked,
      name as keyof PersonalisedLearningSupportPlanData
    );
  };

  const isADHDSelected = formData.learningSupport.includes("Diagnosis of ADHD");

  const isAutismSelected = formData.learningSupport.includes(
    "Diagnosis of Autism Spectrum Disorder"
  );

  return (
    <FormSection title="Learning & Support Information" className={className}>
      <div className="learning-support-container">
        {/* ADHD Diagnosis with Support Type dropdown */}
        <div className="diagnosis-row">
          <label
            className={`checkbox-item ${isADHDSelected ? "selected" : ""}`}
            htmlFor="support-adhd"
          >
            <input
              type="checkbox"
              id="support-adhd"
              name="Diagnosis of ADHD"
              checked={isADHDSelected}
              onChange={(e) =>
                handleLearningCheckboxChange(
                  "Diagnosis of ADHD",
                  e.target.checked
                )
              }
            />
            <div className="checkbox-custom"></div>
            <span className="checkbox-label">Diagnosis of ADHD</span>
          </label>

          {/* Support Type dropdown - positioned to the right of ADHD checkbox */}
          <div className="dropdown-container">
            <label className="dropdown-label" htmlFor="adhdType">
              Support type
            </label>
            <select
              id="adhdType"
              name="adhdType"
              value={formData.adhdType}
              onChange={onTextChange}
              className={`dropdown-select ${!isADHDSelected ? "disabled" : ""}`}
              disabled={!isADHDSelected}
            >
              <option value="">Select support type</option>
              {adhdTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Autism Diagnosis with Level dropdown */}
        <div className="diagnosis-row">
          <label
            className={`checkbox-item ${isAutismSelected ? "selected" : ""}`}
            htmlFor="support-autism"
          >
            <input
              type="checkbox"
              id="support-autism"
              name="Diagnosis of Autism Spectrum Disorder"
              checked={isAutismSelected}
              onChange={(e) =>
                handleLearningCheckboxChange(
                  "Diagnosis of Autism Spectrum Disorder",
                  e.target.checked
                )
              }
            />
            <div className="checkbox-custom"></div>
            <span className="checkbox-label">
              Diagnosis of Autism Spectrum Disorder
            </span>
          </label>

          {/* Level dropdown - positioned to the right of Autism checkbox */}
          <div className="dropdown-container">
            <label className="dropdown-label" htmlFor="autismLevel">
              Level
            </label>
            <select
              id="autismLevel"
              name="autismLevel"
              value={formData.autismLevel}
              onChange={onTextChange}
              className={`dropdown-select ${
                !isAutismSelected ? "disabled" : ""
              }`}
              disabled={!isAutismSelected}
            >
              <option value="">Select level</option>
              {autismLevelOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Plan checkboxes (SpLD + plans) */}
        <div className="plan-checkboxes-container">
          <label
            className={`checkbox-item ${formData.hasSpLD ? "selected" : ""}`}
            htmlFor="support-spld"
          >
            <input
              type="checkbox"
              id="support-spld"
              name="hasSpLD"
              checked={formData.hasSpLD}
              onChange={(e) =>
                handlePlanCheckboxChange("hasSpLD", e.target.checked)
              }
            />
            <div className="checkbox-custom"></div>
            <span className="checkbox-label">
              Has Specific Learning Difficulty (SpLD)
            </span>
          </label>

          <label
            className={`checkbox-item ${formData.hasRMP ? "selected" : ""}`}
            htmlFor="support-rmp"
          >
            <input
              type="checkbox"
              id="support-rmp"
              name="hasRMP"
              checked={formData.hasRMP}
              onChange={(e) =>
                handlePlanCheckboxChange("hasRMP", e.target.checked)
              }
            />
            <div className="checkbox-custom"></div>
            <span className="checkbox-label">
              Has Risk Management Plan (RMP)
            </span>
          </label>

          <label
            className={`checkbox-item ${formData.hasBRP ? "selected" : ""}`}
            htmlFor="support-brp"
          >
            <input
              type="checkbox"
              id="support-brp"
              name="hasBRP"
              checked={formData.hasBRP}
              onChange={(e) =>
                handlePlanCheckboxChange("hasBRP", e.target.checked)
              }
            />
            <div className="checkbox-custom"></div>
            <span className="checkbox-label">
              Has Behaviour Response Plan (BRP)
            </span>
          </label>

          <label
            className={`checkbox-item ${formData.hasBSP ? "selected" : ""}`}
            htmlFor="support-bsp"
          >
            <input
              type="checkbox"
              id="support-bsp"
              name="hasBSP"
              checked={formData.hasBSP}
              onChange={(e) =>
                handlePlanCheckboxChange("hasBSP", e.target.checked)
              }
            />
            <div className="checkbox-custom"></div>
            <span className="checkbox-label">
              Has Behaviour Support Plan (BSP)
            </span>
          </label>
        </div>

        {/* Other input field */}
        <div className="other-input-container">
          <label className="other-input-label" htmlFor="otherLearningSupport">
            Other:
          </label>
          <input
            type="text"
            id="otherLearningSupport"
            name="otherLearningSupport"
            value={formData.otherLearningSupport}
            onChange={onTextChange}
            className="other-input"
          />
        </div>
      </div>
    </FormSection>
  );
};
